
'use client';

import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { ArticleCard } from '@/components/article-card';
import { type Article } from '@/lib/types';
import { NewsSection } from '@/components/news-section';
import { ArticleCarousel } from '@/components/article-carousel';
import Link from 'next/link';
import { getCategoryUrl, getArticleUrl } from '@/lib/articles';
import { parseDate } from '@/lib/utils';
import { ChevronRight, Clock, Video } from 'lucide-react';
import { format } from 'date-fns';
import { dedupeArticles } from '@/lib/dedupe-articles';
import { useDoc, useMemoFirebase, useFirebase } from '@/firebase';
import { doc } from 'firebase/firestore';
import Script from 'next/script';

// Declare Twitter global for TypeScript
declare global {
  interface Window {
    twttr: any;
  }
}

const EXCLUDED_FROM_TOP = [
  'Crime',
  'Sports',
  'Security & Conflict',
  'World',
  'Environment',
  'Economy',
  'Education',
];

function normalize(value?: string) {
  return (value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[^\w\s-]/g, '');
}

function BriefingDate() {
  const [date, setDate] = useState<string | null>(null);
  useEffect(() => {
    setDate(format(new Date(), 'EEEE, d MMMM yyyy'));
  }, []);
  if (!date) return <span className="opacity-0">...</span>;
  return <span className="whitespace-nowrap">{date}</span>;
}

function SidebarPublishDate({ dateString }: { dateString: string }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return <span className="opacity-0">...</span>;

  const date = parseDate(dateString);
  return (
    <>
      {format(date, 'HH:mm')} • {format(date, 'MMM d')}
    </>
  );
}

function SidebarEmbed({ data }: { data?: { url: string; visibility: boolean; type?: string } }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted && data?.visibility && window.twttr && window.twttr.widgets) {
      window.twttr.widgets.load();
    }
  }, [mounted, data]);

  if (!mounted || !data || !data.visibility || !data.url) return null;

  const url = data.url;
  
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // YouTube check
    if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
      const videoId = hostname.includes('youtu.be') 
        ? urlObj.pathname.split('/').filter(Boolean)[0] 
        : urlObj.searchParams.get('v');
        
      if (videoId) {
        return (
          <div className="my-6 rounded-2xl overflow-hidden border border-border/50 bg-black aspect-video shadow-lg">
            <iframe 
              width="100%" 
              height="100%" 
              src={`https://www.youtube.com/embed/${videoId}?rel=0`} 
              title="YouTube embed"
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowFullScreen
              className="w-full h-full"
            ></iframe>
          </div>
        );
      }
    }

    // X / Twitter check
    if (hostname.includes('x.com') || hostname.includes('twitter.com')) {
      const cleanUrl = url.split('?')[0].replace('x.com', 'twitter.com');
      return (
        <div className="my-6 min-h-[250px] overflow-hidden border border-border/50 rounded-2xl bg-card shadow-lg p-0 flex flex-col items-center">
          <blockquote className="twitter-tweet" data-theme="dark" data-chrome="noheader nofooter noborders transparent">
            <a href={cleanUrl}></a>
          </blockquote>
          <Script 
            src="https://platform.twitter.com/widgets.js" 
            strategy="lazyOnload" 
            onLoad={() => {
              if (window.twttr && window.twttr.widgets) {
                window.twttr.widgets.load();
              }
            }}
          />
        </div>
      );
    }

    // Instagram check
    if (hostname.includes('instagram.com')) {
      return (
        <div className="my-6 overflow-hidden border border-border/50 rounded-2xl bg-card shadow-lg">
          <iframe
            src={`${url.split('?')[0]}embed`}
            className="w-full border-none min-h-[450px]"
            scrolling="no"
            allowTransparency
            title="Instagram embed"
          ></iframe>
        </div>
      );
    }

    // Facebook check
    if (hostname.includes('facebook.com')) {
      return (
        <div className="my-6 overflow-hidden border border-border/50 rounded-2xl bg-card shadow-lg">
          <iframe 
            src={`https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(url)}&show_text=true&width=auto`} 
            width="100%" 
            height="auto" 
            className="border-none overflow-hidden min-h-[300px]"
            scrolling="no" 
            frameBorder="0" 
            allowFullScreen={true} 
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            title="Facebook embed"
          ></iframe>
        </div>
      );
    }
  } catch (e) {
    return null;
  }

  return null;
}

const getMobileLayout = (idx: number) => {
  const r = idx % 7;
  if (r === 0) return 'overlay';
  if (r === 3) return 'default';
  return 'side-mobile';
};

export function HomeClient({
  initialArticles = [],
}: {
  initialArticles?: Article[];
}) {
  const searchParams = useSearchParams();
  const categoryParam = searchParams.get('category');
  const countryParam = searchParams.get('country');
  const { firestore } = useFirebase();

  // Fetch Panel Embeds
  const panelsRef = useMemoFirebase(() => {
    if (!firestore) return null;
    return doc(firestore, 'embeds', 'panels');
  }, [firestore]);

  const { data: panelsDoc } = useDoc<any>(panelsRef);
  const panelEmbeds = panelsDoc?.panels || [];

  const fullCoverageArticles = useMemo(() => {
    let pool = [...initialArticles];
    if (categoryParam) {
      pool = pool.filter(a => (a.primaryCategory || '').toLowerCase().trim() === categoryParam.toLowerCase().trim());
    }
    if (countryParam) {
      pool = pool.filter(a => (a.country || '').toLowerCase().trim() === countryParam.toLowerCase().trim());
    }
    return dedupeArticles(pool.sort((a, b) => parseDate(b.publishDate).getTime() - parseDate(a.publishDate).getTime()));
  }, [initialArticles, categoryParam, countryParam]);

  const sections = useMemo(() => {
    const usedFingerprints = new Set<string>();
    const getNext = (filter: (a: Article) => boolean, count: number) => {
      const results: Article[] = [];
      for (const article of fullCoverageArticles) {
        const key = normalize(article.slug) || normalize(article.headline) || article.id;
        if (!key || usedFingerprints.has(key) || !filter(article)) continue;
        usedFingerprints.add(key);
        results.push(article);
        if (results.length >= count) break;
      }
      return results;
    };
    return {
      topStories: getNext(a => !EXCLUDED_FROM_TOP.includes(a.primaryCategory), 4),
      latestReports: getNext(a => true, 10),
      economyArticles: getNext(a => a.primaryCategory === 'Economy', 4),
      crimeArticles: getNext(a => a.primaryCategory === 'Crime', 4),
      educationArticles: getNext(a => a.primaryCategory === 'Education', 6),
      sportsArticles: getNext(a => a.primaryCategory === 'Sports', 8),
      securityArticles: getNext(a => (a.primaryCategory || '').includes('Security'), 6),
      environmentArticles: getNext(a => a.primaryCategory === 'Environment', 5),
      worldArticles: getNext(a => a.primaryCategory === 'World', 11),
    };
  }, [fullCoverageArticles]);

  const { topStories, latestReports, economyArticles, crimeArticles, educationArticles, sportsArticles, securityArticles, environmentArticles, worldArticles } = sections;

  if (categoryParam || countryParam) {
    return (
      <div className="max-w-[1600px] mx-auto px-0 xl:px-6 pt-24 pb-12 w-full">
        <div className="flex items-center gap-1 mb-10 py-2 px-6 md:px-0">
          <h1 className="text-2xl md:text-lg font-medium font-headline text-[hsl(var(--section-header))] capitalize">
            {categoryParam || countryParam}
          </h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2 md:gap-x-2 md:gap-y-3">
          {fullCoverageArticles.map((a, idx) => (
            <ArticleCard key={a.id} article={a} hideCategory className="h-full" mobileLayout={getMobileLayout(idx)} />
          ))}
        </div>
      </div>
    );
  }

  const mainStory = topStories[0];
  const sideStories = topStories.slice(1, 4);

  return (
    <div className="max-w-[1600px] mx-auto px-0 xl:px-6 w-full pb-24 bg-background">
      <div className="grid grid-cols-12 gap-0 xl:gap-8 items-start">
        <div className="col-span-12 xl:col-span-9">
          {topStories.length > 0 && (
            <section className="mb-16 pt-12 md:pt-16">
              <div className="mb-10 px-6 md:px-0">
                <h1 className="text-3xl md:text-4xl font-normal text-foreground font-headline tracking-tighter capitalize mb-3">Afrika Briefing</h1>
                <div className="text-sm md:text-base text-muted-foreground tracking-widest flex items-center gap-3">
                   <div className="h-1 w-12 bg-primary"></div>
                   <BriefingDate />
                </div>
              </div>
              
              <div className="flex items-center gap-3 mb-6 py-1 px-6 md:px-0">
                <Link href="/politics" className="inline-flex items-center gap-1 group">
                  <h2 className="text-2xl md:text-xl font-normal capitalize tracking-tight text-[hsl(var(--section-header))] font-headline">Top stories</h2>
                  <ChevronRight className="h-5 w-5 text-[hsl(var(--section-header))] group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>

              <div className="md:bg-[hsl(var(--investigative-stage-bg))] md:p-8 lg:px-10 lg:py-14 md:rounded-[3rem] md:shadow-2xl">
                <div className="grid grid-cols-12 gap-0 md:gap-6 items-stretch w-full">
                  <div className="col-span-12 lg:col-span-6 relative group/featured">
                    {mainStory && (
                      <ArticleCard 
                        article={mainStory} 
                        priority 
                        mobileLayout={getMobileLayout(0)} 
                        layout="overlay" 
                        className="h-full relative z-10 rounded-[2rem] overflow-hidden" 
                        hideCategory
                      />
                    )}
                  </div>
                  <div className="col-span-12 lg:col-span-6 flex flex-col divide-y divide-white/10">
                    {sideStories.map((a, idx) => (
                      <div key={a.id} className="h-full py-2 first:pt-0 last:pb-0">
                        <ArticleCard 
                          article={a} 
                          layout="horizontal-list" 
                          mobileLayout={getMobileLayout(idx + 1)} 
                          className="h-full" 
                          hideCategory 
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}
          
          {economyArticles.length > 0 && (
            <section className="mb-16 xl:mb-24">
              <div className="flex items-center gap-3 mb-6 py-1 px-6 md:px-0">
                <Link href={getCategoryUrl('Economy')} className="inline-flex items-center gap-1 group">
                  <h2 className="text-2xl md:text-xl font-normal capitalize tracking-tight text-[hsl(var(--section-header))] font-headline">Economy News</h2>
                  <ChevronRight className="h-5 w-5 text-[hsl(var(--section-header))] group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
              <div className="md:bg-[hsl(var(--investigative-stage-bg))] md:p-8 lg:px-10 lg:py-14 md:rounded-[3rem] md:shadow-2xl">
                <div className="grid grid-cols-12 gap-0 md:gap-6 items-stretch w-full">
                  <div className="col-span-12 lg:col-span-6 relative group/featured">
                    <ArticleCard 
                      article={economyArticles[0]} 
                      layout="overlay" 
                      mobileLayout={getMobileLayout(0)} 
                      className="h-full relative z-10 rounded-[2rem] overflow-hidden" 
                      priority={true} 
                      hideCategory={true} 
                    />
                  </div>
                  <div className="col-span-12 lg:col-span-6 flex flex-col divide-y divide-white/10">
                    {economyArticles.slice(1, 4).map((a, idx) => (
                      <div key={a.id} className="h-full py-2 first:pt-0 last:pb-0">
                        <ArticleCard 
                          article={a} 
                          layout="horizontal-list" 
                          mobileLayout={getMobileLayout(idx + 1)} 
                          className="h-full" 
                          hideCategory 
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}

          {crimeArticles.length > 0 && (
            <section className="mb-16 xl:mb-24">
              <div className="flex items-center gap-3 mb-6 py-1 px-6 md:px-0">
                <Link href={getCategoryUrl('Crime')} className="inline-flex items-center gap-1 group">
                  <h2 className="text-2xl md:text-xl font-normal capitalize tracking-tight text-[hsl(var(--section-header))] font-headline">Crime Reports</h2>
                  <ChevronRight className="h-5 w-5 text-[hsl(var(--section-header))] group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
              <div className="md:bg-[hsl(var(--investigative-stage-bg))] md:p-8 lg:px-10 lg:py-14 md:rounded-[3rem] md:shadow-2xl">
                <div className="grid grid-cols-12 gap-0 md:gap-6 items-stretch w-full">
                  <div className="col-span-12 lg:col-span-6 relative group/featured">
                    <ArticleCard 
                      article={crimeArticles[0]} 
                      layout="overlay" 
                      mobileLayout={getMobileLayout(0)} 
                      className="h-full relative z-10 rounded-[2rem] overflow-hidden" 
                      hideCategory 
                    />
                  </div>
                  <div className="col-span-12 lg:col-span-6 flex flex-col divide-y divide-white/10">
                    {crimeArticles.slice(1, 4).map((a, idx) => (
                      <div key={a.id} className="h-full py-2 first:pt-0 last:pb-0">
                        <ArticleCard 
                          article={a} 
                          layout="horizontal-list" 
                          mobileLayout={getMobileLayout(idx + 1)} 
                          className="h-full" 
                          hideCategory 
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}
          
          <div className="xl:hidden">
            <NewsSection title="Latest Reports" articles={latestReports.slice(0, 6)} layout="horizontal-list" />
          </div>

          {educationArticles.length > 0 && (
            <section className="mb-16 xl:mb-24">
              <div className="flex items-center gap-3 mb-6 py-1 px-6 md:px-0">
                <Link href={getCategoryUrl('Education')} className="inline-flex items-center gap-1 group">
                  <h2 className="text-2xl md:text-xl font-normal capitalize tracking-tight text-[hsl(var(--section-header))] font-headline">Education News</h2>
                  <ChevronRight className="h-5 w-5 text-[hsl(var(--section-header))] group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
              <div className="md:bg-[hsl(var(--investigative-stage-bg))] md:p-8 lg:px-10 lg:py-14 md:rounded-[3rem] md:shadow-2xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
                  {educationArticles.slice(0, 6).map((article, idx) => {
                    let layout: any = 'default';
                    if (idx === 0) layout = 'overlay';
                    else if (idx === 1) layout = 'default';
                    else layout = 'horizontal-list';
                    return (
                      <ArticleCard 
                        key={article.id} 
                        article={article} 
                        layout={layout} 
                        mobileLayout={getMobileLayout(idx)} 
                        hideCategory 
                        className="h-full" 
                      />
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          <ArticleCarousel title="Sports Recap" articles={sportsArticles} categoryId="Sports" hideCategory={true} />
          <NewsSection title="Security & Conflict" articles={securityArticles} categoryId="Security & Conflict" layout="horizontal-list" />
          
          {environmentArticles.length > 0 && (
            <section className="mb-16 xl:mb-24">
              <div className="flex items-center gap-3 mb-6 py-1 px-6 md:px-0">
                <Link href={getCategoryUrl('Environment')} className="inline-flex items-center gap-1 group">
                  <h2 className="text-2xl md:text-xl font-normal capitalize tracking-tight text-[hsl(var(--section-header))] font-headline">Suggested For You</h2>
                  <ChevronRight className="h-5 w-5 text-[hsl(var(--section-header))] group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
              <div className="md:bg-[hsl(var(--investigative-stage-bg))] md:p-8 lg:px-10 lg:py-14 md:rounded-[3rem] md:shadow-2xl">
                <div className="grid grid-cols-12 gap-0 md:gap-6 items-stretch w-full">
                  <div className="col-span-12 lg:col-span-6 relative group/featured">
                    <ArticleCard 
                      article={environmentArticles[0]} 
                      layout="overlay" 
                      mobileLayout={getMobileLayout(0)} 
                      className="h-full relative z-10 rounded-[2rem] overflow-hidden" 
                      priority={true} 
                      hideCategory={true} 
                    />
                  </div>
                  <div className="col-span-12 lg:col-span-6 flex flex-col divide-y divide-white/10">
                    {environmentArticles.slice(1, 4).map((a, idx) => (
                      <div key={a.id} className="h-full py-2 first:pt-0 last:pb-0">
                        <ArticleCard 
                          article={a} 
                          layout="horizontal-list" 
                          mobileLayout={getMobileLayout(idx + 1)} 
                          className="h-full" 
                          hideCategory 
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}
          <NewsSection title="World News" articles={worldArticles} categoryId="World" />
        </div>
        <aside className="hidden xl:block xl:col-span-3 sticky top-24 pt-16">
          <div className="space-y-12">
            <div className="border-t-4 border-primary pt-6">
               <h3 className="text-base font-black font-headline capitalize tracking-tighter mb-8 flex items-center gap-2">
                 <Clock className="h-5 w-5 text-primary" /> Latest Reports
               </h3>
               <div className="space-y-4">
                  {latestReports.slice(0, 6).map((a, idx) => (
                    <React.Fragment key={a.id}>
                      <div className="group">
                        <Link href={getCategoryUrl(a.primaryCategory)} className="text-[10px] font-black uppercase tracking-[0.2em] text-primary mb-1 block hover:underline">{a.primaryCategory}</Link>
                        <Link href={getArticleUrl(a)} className="block">
                          <h4 className="text-sm font-normal leading-snug group-hover:text-primary transition-colors line-clamp-3">{a.headline}</h4>
                        </Link>
                        <p className="text-[10px] text-muted-foreground mt-2 font-normal tracking-wider">
                          <SidebarPublishDate dateString={a.publishDate} />
                        </p>
                      </div>
                      
                      {/* Embed Panel logic: Index 0 after 2nd article (idx 1), Index 1 after 5th article (idx 4) */}
                      {idx === 1 && panelEmbeds[0] && <SidebarEmbed data={panelEmbeds[0]} />}
                      {idx === 4 && panelEmbeds[1] && <SidebarEmbed data={panelEmbeds[1]} />}
                    </React.Fragment>
                  ))}
               </div>
               <Link href="/politics" className="mt-10 block text-center py-3 border border-border rounded-xl text-xs font-black uppercase tracking-widest hover:bg-primary hover:text-white transition-all">View Full Feed</Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
